import React, {useCallback, useEffect, useMemo, useState} from "react";
import {ConsumableData} from "../../../../types/ConsumableData";
import {fetchSelectOptionsByContractNO} from "../../../../backendInteraction/ApiFetch";
import {numberValidator} from "../../../../utils/Validator";
import {Button, Input, message, Select, Space, Table} from "antd";
import {
    addConsumableRow, deleteConsumableByConsId,
    fetchConsumableByPid,
    updateConsumableRow
} from "../../../../backendInteraction/ConsumablesApiFetch";
import {ColumnsType} from "antd/es/table";

interface ConsumablesTableProps {
    data: ConsumableData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
    holeId: string;
    setHoleId: (value: string) => void;
}

const ConsumablesTable: React.FC<ConsumablesTableProps> = ({ data, plodId, contractNo, isView, isAddNewRow, setIsAddNewRow, holeId, setHoleId }) => {
    const [consumablesData, setConsumablesData] = useState<ConsumableData[]>([]);
    const [itemOptions, setItemOptions] = useState<{ value: string; label: string }[]>([]);
    const [editingConsId, setEditingConsId] = useState<number | null>(null);
    const [editData, setEditData] = useState<ConsumableData | null>(null);

    const loadConsumables = useCallback(async () => {
        if (contractNo) {
            const items = await fetchSelectOptionsByContractNO(contractNo, "Consumables");
            setItemOptions(items.map(item => ({ value: item, label: item })));
        } else {
            setItemOptions([]);
        }
    }, [contractNo]);

    useEffect(() => {
        loadConsumables();
    }, [loadConsumables]);

    useEffect(() => {
        setConsumablesData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingConsId !== -1) {
            const len = consumablesData.length;
            const newRow: ConsumableData = {
                key: -1,
                cons_id: -1,
                hole_id: len > 0 ? consumablesData[len - 1].hole_id || holeId : holeId,
                item: "",
                quantity: "",
                pid: plodId ?? -1,
            };
            setConsumablesData(prevData => {
                const hasNewRow = prevData.some(item => item.cons_id === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            });

            setEditingConsId(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingConsId]);

    useEffect(() => {
        if (isView) {
            if (editingConsId === -1) {
                setConsumablesData(prevData => prevData.filter(item => item.cons_id !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingConsId(null);
            setEditData(null);
        }
    }, [isView]);

    const saveChanges = useCallback(async () => {
        console.log("Saving changes:", editData);

        if (!editData) return;

        const { quantity, hole_id } = editData;

        if(!!quantity) {
            const isNumber = numberValidator(quantity, 'Quantity');
            if (!isNumber) return;
            const q = parseFloat(quantity);
            if (q < 0) {
                message.error('The "Quantity" value cannot be less than 0.');
                return;
            }
        }

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingConsId === -1) {
            const success = await addConsumableRow(editData, aid);
            if (success) {
                setConsumablesData(await fetchConsumableByPid(plodId ?? -1, aid));
                message.success("New consumable added successfully!");
            } else {
                message.error("Failed to add new consumable.");
            }
        } else {
            const success = await updateConsumableRow(editingConsId ?? -1, editData);
            if (success) {
                setConsumablesData(await fetchConsumableByPid(plodId ?? -1, aid));
                message.success("Consumable updated successfully!");
            } else {
                message.error("Failed to update consumable.");
            }
        }

        !(hole_id === holeId) && setHoleId(hole_id);

        if (editingConsId === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingConsId(null);
        setEditData(null);
    }, [editData, editingConsId, setIsAddNewRow]);

    const cancelEditing = useCallback((record: ConsumableData) => {
        console.log("Cancel editing");
        setEditingConsId(null);
        setEditData(null);

        if (record.cons_id === -1) {
            setConsumablesData(prevData => prevData.filter(item => item.cons_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: ConsumableData) => {
        console.log("Editing row:", record);

        if (editingConsId === -1) {
            setConsumablesData(prevData => prevData.filter(item => item.cons_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingConsId(prevId => {
            if (prevId !== null) cancelEditing({ cons_id: prevId } as ConsumableData);
            return record.cons_id;
        });

        setEditData({ ...record });
    }, [editingConsId, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: ConsumableData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deleteConsumableByConsId(record.cons_id);
        if (success) {
            setConsumablesData(await fetchConsumableByPid(plodId ?? -1, aid));
            message.success("Consumable deleted successfully!");
        } else {
            message.error("Failed to delete consumable.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof ConsumableData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);

    const columns: ColumnsType<ConsumableData> = useMemo(() => [
        {
            title: "Hole ID",
            dataIndex: "hole_id",
            key: "hole_id",
            width: "20%",
            render: (_, record) =>
                editingConsId === record.cons_id ? (
                    <Input
                        value={editData?.hole_id}
                        onChange={(e) => handleInputChange("hole_id", e.target.value)}
                    />
                ) : (
                    record.hole_id
                ),
        },
        {
            title: "Item",
            dataIndex: "item",
            key: "item",
            width: "40%",
            minWidth: 280,
            render: (_, record) =>
                editingConsId === record.cons_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={itemOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.item || ""}
                        onChange={(value) => handleInputChange("item", value || "")}
                    />
                ) : (
                    record.item
                ),
        },
        {
            title: "Quantity",
            dataIndex: "quantity",
            key: "quantity",
            width: "20%",
            render: (_, record) =>
                editingConsId === record.cons_id ? (
                    <Input
                        value={editData?.quantity}
                        onChange={(e) => handleInputChange("quantity", e.target.value)}
                    />
                ) : (
                    record.quantity
                ),
        },
        ...(!isView
            ? [
                {
                    title: "Action",
                    key: "action",
                    width: "20%",
                    render: (_, record) => (
                        <Space size="small">
                            <Button color="geekblue" variant="text"
                                    onClick={() => (editingConsId === record.cons_id ? saveChanges() : startEditing(record))}>
                                {editingConsId === record.cons_id ? "Save" : "Edit"}
                            </Button>
                            <Button color="geekblue" variant="text"
                                    onClick={() => (editingConsId === record.cons_id ? cancelEditing(record) : deleteRow(record))}>
                                {editingConsId === record.cons_id ? "Cancel" : "Delete"}
                            </Button>
                        </Space>
                    ),
                },
            ] as ColumnsType<ConsumableData>
            : []),
    ], [isView, editingConsId, itemOptions, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<ConsumableData>
            columns={columns}
            pagination={false}
            dataSource={consumablesData}
        />
    );
};

export default ConsumablesTable;